import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { DateTimePickerButton } from '@/components/common/DateTimePickerButton';
import { usePropertiesContext } from '@/context/PropertiesProvider';

interface UserTask {
  id: string;
  text: string;
  notes: string;
  date: string | null;
  done: boolean;
}

const STORAGE_PREFIX = 'homehq:owner-tasks:';
const LIME = '#c0ff02';
const DOTTED = 'rgba(255,255,255,0.18)';
const FONT = "'Manrope', sans-serif";

const readTasks = (key: string): UserTask[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (t): t is Partial<UserTask> & { id: string; text: string; done: boolean } =>
          typeof t?.id === 'string' &&
          typeof t?.text === 'string' &&
          typeof t?.done === 'boolean' &&
          (t.date === null || typeof t.date === 'string' || t.date === undefined),
      )
      .map((t) => ({
        id: t.id,
        text: t.text,
        notes: typeof t.notes === 'string' ? t.notes : '',
        date: t.date ?? null,
        done: t.done,
      }));
  } catch {
    return [];
  }
};

const writeTasks = (key: string, tasks: UserTask[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(tasks));
  } catch {
    // ignore
  }
};

const fmtDate = (iso: string | null) => {
  if (!iso) return null;
  const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const hasTime = iso.includes('T') && !(d.getHours() === 0 && d.getMinutes() === 0);
  if (!hasTime) return datePart;
  const timePart = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${datePart} · ${timePart}`;
};

/* ─── StickyNotesCard ──────────────────────────────────────── */

export const StickyNotesCard = () => {
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const storageKey = propertyId ? `${STORAGE_PREFIX}${propertyId}` : null;

  const [tasks, setTasks] = useState<UserTask[]>([]);

  // Add-form state
  const [isAdding, setIsAdding] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftDate, setDraftDate] = useState<Date | null>(null);
  const [draftTime, setDraftTime] = useState<Date | null>(null);
  const subjectInputRef = useRef<HTMLInputElement | null>(null);

  // Edit-in-place state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editDate, setEditDate] = useState<Date | null>(null);
  const [editTime, setEditTime] = useState<Date | null>(null);
  const editInputRef = useRef<HTMLInputElement | null>(null);

  const initializedRef = useRef(false);
  const instanceId = useRef(`sticky-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    initializedRef.current = false;
    setTasks(storageKey ? readTasks(storageKey) : []);
  }, [storageKey]);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    if (storageKey) {
      writeTasks(storageKey, tasks);
      window.dispatchEvent(
        new CustomEvent('hauser:tasks-updated', { detail: { source: instanceId.current } }),
      );
    }
  }, [storageKey, tasks]);

  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail?.source === instanceId.current) return;
      setTasks(storageKey ? readTasks(storageKey) : []);
    };
    window.addEventListener('hauser:tasks-updated', handler);
    return () => window.removeEventListener('hauser:tasks-updated', handler);
  }, [storageKey]);

  useEffect(() => {
    if (isAdding && subjectInputRef.current) subjectInputRef.current.focus();
  }, [isAdding]);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const toIso = (d: Date | null, t: Date | null): string | null => {
    if (!d || isNaN(d.getTime())) return null;
    if (t && !isNaN(t.getTime())) {
      const combined = new Date(d);
      combined.setHours(t.getHours(), t.getMinutes(), 0, 0);
      return combined.toISOString();
    }
    return d.toISOString().split('T')[0];
  };

  const openAdd = () => {
    setDraftText('');
    setDraftNotes('');
    setDraftDate(null);
    setDraftTime(null);
    setIsAdding(true);
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setDraftText('');
    setDraftNotes('');
    setDraftDate(null);
    setDraftTime(null);
  };

  const commitAdd = () => {
    const text = draftText.trim();
    if (!text) {
      cancelAdd();
      return;
    }
    setTasks((prev) => [
      {
        id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        text,
        notes: draftNotes.trim(),
        date: toIso(draftDate, draftTime),
        done: false,
      },
      ...prev,
    ]);
    cancelAdd();
  };

  const beginEdit = (task: UserTask) => {
    setEditingId(task.id);
    setEditText(task.text);
    setEditNotes(task.notes);
    if (task.date) {
      const d = new Date(task.date.includes('T') ? task.date : `${task.date}T00:00:00`);
      setEditDate(d);
      setEditTime(task.date.includes('T') ? d : null);
    } else {
      setEditDate(null);
      setEditTime(null);
    }
  };

  const commitEdit = () => {
    if (!editingId) return;
    const text = editText.trim();
    if (!text) {
      setEditingId(null);
      return;
    }
    setTasks((prev) =>
      prev.map((t) =>
        t.id === editingId
          ? { ...t, text, notes: editNotes.trim(), date: toIso(editDate, editTime) }
          : t,
      ),
    );
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const toggleDone = (id: string) =>
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));

  const removeTask = (id: string) => setTasks((prev) => prev.filter((t) => t.id !== id));

  /* ─── Render ────────────────────────────────────────────── */

  const inputBaseSx = {
    width: '100%',
    padding: '8px 12px',
    background: 'var(--tx-fill)',
    border: 0,
    borderRadius: '12px',
    color: 'var(--tx-text)',
    fontFamily: FONT,
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': { background: 'var(--tx-fill-2)' },
    '&::placeholder': { color: 'rgba(255,255,255,0.4)' },
  } as const;

  return (
    <Box>
      {/* + Add Task CTA / Accordion form */}
      <Box sx={{ pt: '12px', pb: '4px' }}>
        {!isAdding ? (
          <Box
            component="button"
            type="button"
            onClick={openAdd}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '10px 12px',
              background: 'var(--tx-fill)',
              border: '1px dashed var(--tx-line)',
              borderRadius: '8px',
              color: 'var(--tx-muted)',
              fontFamily: FONT,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s',
              '&:hover': { background: 'rgba(255,255,255,0.08)', color: 'var(--tx-text)' },
            }}>
            <AddIcon sx={{ fontSize: 10, color: LIME }} />
            Add task
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box
              component="input"
              ref={subjectInputRef as React.Ref<HTMLInputElement>}
              type="text"
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitAdd();
                if (e.key === 'Escape') cancelAdd();
              }}
              placeholder="Title"
              sx={{ ...inputBaseSx, fontSize: 14, height: 36 }}
            />
            <Box
              component="textarea"
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') cancelAdd();
              }}
              placeholder="Notes (optional)"
              rows={2}
              style={{ fontFamily: FONT, fontSize: 13 }}
              sx={{ ...inputBaseSx, fontSize: 13, resize: 'none', lineHeight: 1.5, py: '8px' }}
            />
            <DateTimePickerButton
              date={draftDate}
              time={draftTime}
              onConfirm={(d, t) => {
                setDraftDate(d);
                setDraftTime(t);
              }}
            />
            <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', mt: '10px' }}>
              <Box
                component="button"
                type="button"
                onClick={cancelAdd}
                sx={{
                  height: 40,
                  padding: '0 24px',
                  background: 'var(--tx-fill)',
                  color: 'var(--tx-muted)',
                  border: 0,
                  borderRadius: '10px',
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  cursor: 'pointer',
                  '&:hover': { color: 'var(--tx-text)', background: 'var(--tx-fill-2)' },
                }}>
                Cancel
              </Box>
              <Box
                component="button"
                type="button"
                onClick={commitAdd}
                disabled={!draftText.trim()}
                sx={{
                  height: 40,
                  padding: '0 24px',
                  background: 'var(--tx-fill-2)',
                  color: 'var(--tx-text)',
                  border: 'none',
                  borderRadius: '10px',
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity 0.15s',
                  letterSpacing: '-0.02em',
                  '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
                }}>
                Add
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {tasks.length === 0 ? (
        !isAdding && (
          <Box sx={{ padding: '20px 4px', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
              No tasks yet. Add one above.
            </Typography>
          </Box>
        )
      ) : (
        <Box sx={{ pt: '12px' }}>
          {tasks.map((t, idx) => {
            const isLast = idx === tasks.length - 1;
            const isEditing = editingId === t.id;
            return (
              <Box
                key={t.id}
                sx={{
                  position: 'relative',
                  ...(!isLast ? { borderBottom: '1px solid rgba(255,255,255,0.07)' } : {}),
                }}>
                {!isLast && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 11,
                      top: '100%',
                      bottom: -1,
                      width: 0,
                      borderLeft: `2px dotted ${DOTTED}`,
                    }}
                  />
                )}

                {isEditing ? (
                  /* Edit mode — full-width form */
                  <Box sx={{ display: 'flex', gap: '14px', py: '10px' }}>
                    <Box sx={{ width: 24, flexShrink: 0 }} />
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                      }}>
                      <Box
                        component="input"
                        ref={editInputRef as React.Ref<HTMLInputElement>}
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEdit();
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        onBlur={commitEdit}
                        sx={{ ...inputBaseSx, fontSize: 14, height: 30 }}
                      />
                      <Box
                        component="textarea"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        placeholder="Notes (optional)"
                        rows={2}
                        style={{ fontFamily: FONT, fontSize: 12 }}
                        sx={{
                          ...inputBaseSx,
                          fontSize: 12,
                          resize: 'none',
                          lineHeight: 1.5,
                          py: '6px',
                        }}
                      />
                      <DateTimePickerButton
                        date={editDate}
                        time={editTime}
                        onConfirm={(d, t) => {
                          setEditDate(d);
                          setEditTime(t);
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Title row — circle + title + X all centered on same line */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        pt: '12px',
                        pb: t.notes || t.date ? 0 : '12px',
                      }}>
                      <Box
                        onClick={() => toggleDone(t.id)}
                        sx={{
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          cursor: 'pointer',
                          zIndex: 1,
                          backgroundColor: 'background.default',
                          borderRadius: '50%',
                        }}>
                        {t.done ? (
                          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <circle cx="11" cy="11" r="11" fill={LIME} />
                            <polyline
                              points="6.5,11 9.5,14 15.5,8"
                              stroke="#262626"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              fill="none"
                            />
                          </svg>
                        ) : (
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: '#404040E6',
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        onClick={() => beginEdit(t)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontWeight: 600,
                          fontSize: 13,
                          color: 'var(--tx-text)',
                          fontFamily: FONT,
                          textDecoration: t.done ? 'line-through' : 'none',
                          opacity: t.done ? 0.6 : 1,
                          cursor: 'text',
                          wordBreak: 'break-word',
                        }}>
                        {t.text}
                      </Typography>
                      <IconButton
                        onClick={() => removeTask(t.id)}
                        aria-label="delete task"
                        size="small"
                        sx={{
                          color: 'var(--tx-faint)',
                          padding: '4px',
                          flexShrink: 0,
                          '&:hover': { color: 'var(--tx-text)' },
                        }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>

                    {/* Notes + date — indented to align with title */}
                    {(t.notes || t.date) && (
                      <Box sx={{ pl: '38px', pr: '32px', pb: '10px' }}>
                        {t.notes && (
                          <Typography
                            onClick={() => beginEdit(t)}
                            sx={{
                              color: '#B0B0B0',
                              fontSize: 11,
                              fontFamily: FONT,
                              opacity: t.done ? 0.5 : 1,
                              cursor: 'text',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}>
                            {t.notes}
                          </Typography>
                        )}
                        {t.date && (
                          <Typography
                            sx={{
                              color: 'var(--tx-faint)',
                              fontSize: 11,
                              fontFamily: FONT,
                              mt: '2px',
                              opacity: t.done ? 0.5 : 1,
                            }}>
                            {fmtDate(t.date)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default StickyNotesCard;
